// Inspecciones (F3a, M41), oculta para clientes como el Plan de Emergencias v2: pestañas Inspecciones (tabla tipo
// Monday y calendario), Nueva o programar, Hallazgos y Plantillas. Al abrir una inspección se ejecuta (EjecutarInspeccion).
// Roles: viewer mira; admin y manager crean y editan; admin borra y reabre (como en la base). Sedes: con una sola sede
// activa no hay selector ni filtro (regla de F1a). Después de cada cambio se vuelve a calcular el Checklist (PO-14,
// PO-15 y PO-23) y, si una inspección de una programación se completa o se cancela, se genera la siguiente.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompany } from '../../CompanyContext';
import { useImpersonation } from '../../ImpersonationContext';
import { useSedes } from '../../lib/useSedes';
import { syncInspeccionesV2Compliance } from '../../lib/inspecciones/insp-checklist';
import {
  cargarActivosMin, cargarHallazgos, cargarInspecciones, cargarPlantillas, cargarProgramaciones, cargarTrabajadores, generarSiguiente,
} from '../../lib/inspecciones/insp-datos';
import { resumenHallazgos } from '../../lib/inspecciones/insp-logica';
import type { ActivoMin, FilaHallazgo, FilaInspeccion, FilaPlantilla, FilaProgramacion, SedeMin, TrabajadorMin } from '../../lib/inspecciones/insp-tipos';
import ProgramaAnualPanel from '../../components/programas/ProgramaAnualPanel';
import { programasDisponibles } from '../../lib/programas/prog-datos';
import EjecutarInspeccion from './EjecutarInspeccion';
import HallazgosInsp from './HallazgosInsp';
import PlantillasInsp from './PlantillasInsp';
import ProgramarInsp from './ProgramarInsp';
import TablaInspecciones from './TablaInspecciones';
import '../pe/PlanEmergenciasV2.css';
import './InspeccionesV2.css';

type Pestana = 'inspecciones' | 'nueva' | 'hallazgos' | 'plantillas';
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function InspeccionesV2({ hoy: hoyProp }: { hoy?: Date } = {}) {
  const hoy = useMemo(() => hoyProp ?? new Date(), [hoyProp]);
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const empresa = impersonated ?? company;
  const companyId = empresa?.id ?? '';
  const puedeEditar = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const puedeBorrar = profile?.role === 'admin' || !!impersonated;
  const usuarioId = profile?.id ?? null;
  const { sedes, varias } = useSedes(companyId);
  const sedesMin: SedeMin[] = useMemo(() => sedes.map(s => ({ id: s.id, name: s.name, active: s.active, is_main: s.is_main, canton_code: s.canton_code })), [sedes]);

  const [pestana, setPestana] = useState<Pestana>('inspecciones');
  const [plantillas, setPlantillas] = useState<FilaPlantilla[]>([]);
  const [inspecciones, setInspecciones] = useState<FilaInspeccion[]>([]);
  const [hallazgos, setHallazgos] = useState<FilaHallazgo[]>([]);
  const [programaciones, setProgramaciones] = useState<FilaProgramacion[]>([]);
  const [trabajadores, setTrabajadores] = useState<TrabajadorMin[]>([]);
  const [activos, setActivos] = useState<ActivoMin[]>([]);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  // Lote C (PO 14): programa anual de inspecciones, con la M48 en la base.
  const [programasOk, setProgramasOk] = useState(false);
  useEffect(() => { if (companyId) programasDisponibles(companyId).then(setProgramasOk).catch(() => setProgramasOk(false)); }, [companyId]);

  const recargar = useCallback(async () => {
    if (!companyId) return null;
    try {
      const [t, i, h, s] = await Promise.all([cargarPlantillas(companyId), cargarInspecciones(companyId), cargarHallazgos(companyId), cargarProgramaciones(companyId)]);
      setPlantillas(t); setInspecciones(i); setHallazgos(h); setProgramaciones(s); setError('');
      return { t, i, s };
    } catch (e) { setError(msg(e)); return null; }
  }, [companyId]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    (async () => {
      await recargar();
      const [w, a] = await Promise.all([cargarTrabajadores(companyId).catch(() => []), cargarActivosMin(companyId).catch(() => [])]);
      if (vivo) { setTrabajadores(w); setActivos(a ?? []); setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [recargar, companyId]);

  // Checklist (PO-14, PO-15 y PO-23): igual que el plan, sin esperar ni bloquear la pantalla.
  const sincronizar = useCallback(() => { if (puedeEditar) syncInspeccionesV2Compliance(companyId, hoy).catch(() => {}); }, [companyId, hoy, puedeEditar]);
  const cambio = useCallback(async () => { await recargar(); sincronizar(); }, [recargar, sincronizar]);

  // Una inspección de una programación terminó (firmada o cancelada): se genera la siguiente.
  const alCambiarInspeccion = useCallback(async (i: FilaInspeccion | null, evento?: 'firmada' | 'cancelada') => {
    const datos = await recargar();
    if (i && evento && i.schedule_id && datos) {
      const p = datos.s.find(x => x.id === i.schedule_id);
      if (p) {
        try {
          const nombre = datos.t.find(t => t.id === p.template_id)?.name ?? i.template_name;
          if (await generarSiguiente(companyId, p, datos.i, nombre, usuarioId)) await recargar();
        } catch (e) { setError(msg(e)); }
      }
    }
    sincronizar();
  }, [recargar, sincronizar, companyId, usuarioId]);

  const actual = abierta ? inspecciones.find(i => i.id === abierta) ?? null : null;
  const resumen = resumenHallazgos(hallazgos, hoy);

  if (!companyId) return <div className="pe2"><p className="pe2-nota">Elige una empresa.</p></div>;

  return (
    <div className="pe2">
      <div>
        <span className="pe2-paso">Procedimientos operativos</span>
        <h2>Inspecciones</h2>
        <p className="pe2-sub">Plantillas, inspecciones programadas o hechas en el momento, hallazgos y su seguimiento con evidencia.</p>
      </div>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {cargando ? <p className="pe2-nota">Cargando…</p> : actual ? (
        <EjecutarInspeccion inspeccion={actual} empresa={empresa?.name ?? ''} sedes={sedesMin} varias={varias} trabajadores={trabajadores} hallazgos={hallazgos}
          puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} usuarioId={usuarioId} hoy={hoy}
          onCambio={alCambiarInspeccion} onVolver={() => setAbierta(null)} />
      ) : (
        <>
          <div className="pe2-res" aria-label="Resumen">
            <div><b>{inspecciones.filter(i => i.status === 'programada' || i.status === 'en_curso').length}</b><span>Por hacer</span></div>
            <div><b>{inspecciones.filter(i => i.status === 'completada').length}</b><span>Completadas</span></div>
            <div><b>{resumen.abiertos}</b><span>Hallazgos abiertos</span></div>
            <div><b className={resumen.vencidos ? 'insp-num-alerta' : undefined}>{resumen.vencidos}</b><span>Hallazgos vencidos</span></div>
          </div>
          <div className="pe2-vistas" role="tablist" aria-label="Secciones de Inspecciones">
            {([['inspecciones', 'Inspecciones'], ['nueva', puedeEditar ? 'Nueva o programar' : 'Programaciones'], ['hallazgos', 'Hallazgos'], ['plantillas', 'Plantillas']] as [Pestana, string][]).map(([k, t]) => (
              <button key={k} type="button" role="tab" aria-selected={pestana === k} className={pestana === k ? 'on' : ''} onClick={() => setPestana(k)}>{t}</button>
            ))}
          </div>
          {pestana === 'inspecciones' && (
            <TablaInspecciones inspecciones={inspecciones} hallazgos={hallazgos} sedes={sedesMin} varias={varias} trabajadores={trabajadores} activos={activos}
              hoy={hoy} onAbrir={setAbierta} />
          )}
          {pestana === 'nueva' && (
            <ProgramarInsp companyId={companyId} plantillas={plantillas} programaciones={programaciones} inspecciones={inspecciones} sedes={sedesMin} varias={varias}
              trabajadores={trabajadores} puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} usuarioId={usuarioId} hoy={hoy} onCambio={cambio}
              onCreada={async (i, abrir) => { await cambio(); if (abrir) setAbierta(i.id); }} />
          )}
          {pestana === 'nueva' && programasOk && (
            <ProgramaAnualPanel tipo="inspecciones" companyId={companyId} empresa={empresa?.name ?? ''} puedeEditar={puedeEditar} />
          )}
          {pestana === 'hallazgos' && (
            <HallazgosInsp companyId={companyId} hallazgos={hallazgos} inspecciones={inspecciones} sedes={sedesMin} varias={varias} trabajadores={trabajadores}
              puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} usuarioId={usuarioId} onCambio={cambio} hoy={hoy} />
          )}
          {pestana === 'plantillas' && (
            <PlantillasInsp companyId={companyId} plantillas={plantillas} puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} usuarioId={usuarioId} onCambio={cambio} />
          )}
        </>
      )}
    </div>
  );
}
